'use client';

import React, { useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronRight, GripVertical, ToggleLeft, ToggleRight } from 'lucide-react';
import type { PayloadField } from '@/lib/types';
import { PAYLOAD_TOKENS } from '@/lib/payloadBuilder';

interface Props {
  fields: PayloadField[];
  onChange: (fields: PayloadField[]) => void;
}

export default function PayloadFieldsEditor({ fields, onChange }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [showTokens, setShowTokens] = useState(false);

  const addField = () => {
    const newField: PayloadField = {
      id: `pf-${Date.now()}`,
      key: '',
      value: '',
      enabled: true,
    };
    onChange([...fields, newField]);
  };

  const updateField = (id: string, patch: Partial<PayloadField>) => {
    onChange(fields.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  };

  const removeField = (id: string) => {
    onChange(fields.filter((f) => f.id !== id));
  };

  const toggleField = (id: string) => {
    onChange(fields.map((f) => (f.id === id ? { ...f, enabled: !f.enabled } : f)));
  };

  const enabledCount = fields.filter((f) => f.enabled).length;

  return (
    <div className="rounded-lg border border-border overflow-hidden" style={{ background: 'var(--input)' }}>
      {/* Header toggle */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-muted/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          {expanded ? <ChevronDown size={14} className="text-muted-foreground" /> : <ChevronRight size={14} className="text-muted-foreground" />}
          <span className="text-xs font-semibold text-foreground">Payload Fields</span>
          <span className="text-2xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full">
            {enabledCount} active
          </span>
        </div>
        <span className="text-2xs text-muted-foreground">Customise the JSON sent per item</span>
      </button>

      {expanded && (
        <div className="border-t border-border px-4 pb-4 pt-3 flex flex-col gap-3">
          {/* Token reference toggle */}
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setShowTokens((v) => !v)}
              className="text-2xs text-primary hover:underline"
            >
              {showTokens ? 'Hide' : 'Show'} available tokens
            </button>
            <button
              type="button"
              onClick={addField}
              className="flex items-center gap-1 text-2xs font-medium btn-ghost px-2 py-1 rounded-md border border-border"
            >
              <Plus size={11} />
              Add Field
            </button>
          </div>

          {/* Token reference */}
          {showTokens && (
            <div className="rounded-lg bg-muted/50 p-3 grid grid-cols-2 gap-1.5">
              {PAYLOAD_TOKENS.map((t) => (
                <div key={t.token} className="flex items-start gap-1.5">
                  <code className="text-2xs font-mono-data text-primary flex-shrink-0">{t.token}</code>
                  <span className="text-2xs text-muted-foreground">{t.description}</span>
                </div>
              ))}
            </div>
          )}

          {/* Fields list */}
          {fields.length === 0 ? (
            <p className="text-2xs text-muted-foreground text-center py-3">
              No custom fields. The default payload structure will be used.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {fields.map((field) => (
                <div
                  key={field.id}
                  className={`flex items-center gap-2 p-2 rounded-lg border transition-opacity ${
                    field.enabled ? 'border-border' : 'border-border/50 opacity-50'
                  }`}
                  style={{ background: 'var(--background)' }}
                >
                  <GripVertical size={12} className="text-muted-foreground flex-shrink-0 cursor-grab" />

                  {/* Key */}
                  <input
                    type="text"
                    value={field.key}
                    onChange={(e) => updateField(field.id, { key: e.target.value })}
                    placeholder="field_key"
                    className="input-base px-2 py-1 text-2xs font-mono-data w-28 flex-shrink-0"
                  />

                  <span className="text-2xs text-muted-foreground flex-shrink-0">:</span>

                  {/* Value */}
                  <input
                    type="text"
                    value={field.value}
                    onChange={(e) => updateField(field.id, { value: e.target.value })}
                    placeholder="value or {{token}}"
                    className="input-base px-2 py-1 text-2xs font-mono-data flex-1 min-w-0"
                  />

                  {/* Toggle enabled */}
                  <button
                    type="button"
                    onClick={() => toggleField(field.id)}
                    className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                    title={field.enabled ? 'Disable field' : 'Enable field'}
                  >
                    {field.enabled ? (
                      <ToggleRight size={16} className="text-primary" />
                    ) : (
                      <ToggleLeft size={16} />
                    )}
                  </button>

                  {/* Remove */}
                  <button
                    type="button"
                    onClick={() => removeField(field.id)}
                    className="flex-shrink-0 text-muted-foreground hover:text-danger transition-colors"
                    title="Remove field"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <p className="text-2xs text-muted-foreground">
            Use <code className="font-mono-data text-primary">{'{{token}}'}</code> syntax to inject item data. Static values are passed as-is.
          </p>
        </div>
      )}
    </div>
  );
}
